// Trendyol liste sablonu.
//
// Etsy sablonundan yapisal olarak farkli: Etsy'de taksonomi + kargo profili +
// who_made/when_made gibi alanlar var; Trendyol'da ise KATEGORI secilir ve o
// kategorinin ZORUNLU OZELLIKLERI doldurulur. Zorunlu alanlar kategoriden
// kategoriye degistigi icin form sabit degil, API'den gelen semaya gore
// uretilir.

import { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Check, AlertCircle, ArrowLeft, Save, Info } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";

interface CategoryRow { id: number; name: string; path: string; leaf: boolean; parentId: number | null }
interface AttrValue { id: number | null; name: string }
interface CategoryAttribute {
  id: number | null;
  name: string;
  required: boolean;
  allowCustom: boolean;
  varianter: boolean;
  slicer: boolean;
  values: AttrValue[];
}

/** Zorunlu ozelligin degeri nereden gelir */
type AttrSource = "fixed" | "renk" | "beden" | "materyal";

const SOURCE_LABEL: Record<AttrSource, string> = {
  fixed: "Şablonda sabit",
  renk: "Üründen: Renk",
  beden: "Üründen: Beden / zincir uzunluğu",
  materyal: "Üründen: Materyal",
};

/** Ozellik adindan makul bir kaynak tahmini — kullanici degistirebilir.
 *  Renk ve beden urun basina degistigi icin sablonda sabitlenmemeli. */
function guessSource(a: { name: string; varianter: boolean }): AttrSource {
  const n = a.name.toLowerCase();
  if (/renk|color/.test(n)) return "renk";
  if (/beden|size|uzunluk/.test(n)) return "beden";
  if (/materyal|malzeme|material/.test(n)) return "materyal";
  return a.varianter ? "renk" : "fixed";
}

export function TrendyolTemplateEditor({ onBack, onSaved }: { onBack: () => void; onSaved: () => void }) {
  const [cats, setCats] = useState<CategoryRow[]>([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [catsError, setCatsError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [attrs, setAttrs] = useState<CategoryAttribute[]>([]);
  const [attrsLoading, setAttrsLoading] = useState(false);
  const [attrsError, setAttrsError] = useState<string | null>(null);

  // attributeId -> { valueId?, value? }
  const [picked, setPicked] = useState<Record<string, { valueId?: number; value?: string }>>({});
  // Bir ozellik ya sablonda SABIT tutulur ya da her urunde degistigi icin
  // Listing Builder tarafindan katalog urununden doldurulur (renk, beden…).
  const [sources, setSources] = useState<Record<string, AttrSource>>({});

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("100");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Listing Builder'i on dolduran kurallar. Sablonun attributes alaninda
  // saklanir; builder katalog urununun degerlerini kaliba yerlestirir.
  const [titlePattern, setTitlePattern] = useState("");
  const [descriptionPattern, setDescriptionPattern] = useState("");
  const [priceMultiplier, setPriceMultiplier] = useState("2.2");
  const [commissionPercent, setCommissionPercent] = useState("21");

  useEffect(() => {
    (async () => {
      setCatsLoading(true); setCatsError(null);
      try {
        const res = await fetch(`${getBackendUrl()}/api/trendyol/categories`, { headers: authHeaders() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Kategoriler alınamadı");
        setCats(body.data as CategoryRow[]);
      } catch (e) {
        setCatsError(e instanceof Error ? e.message : "Kategoriler alınamadı");
      } finally {
        setCatsLoading(false);
      }
    })();
  }, []);

  // Urun yalnizca YAPRAK kategoriye acilabilir; arama listesinde de onlar cikar
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const leaves = cats.filter((c) => c.leaf);
    if (!needle) return leaves.slice(0, 40);
    return leaves.filter((c) => c.path.toLowerCase().includes(needle)).slice(0, 60);
  }, [cats, q]);

  const selectedCat = cats.find((c) => c.id === categoryId) ?? null;

  async function loadAttributes(id: number) {
    setAttrsLoading(true); setAttrsError(null); setAttrs([]); setPicked({});
    try {
      const res = await fetch(`${getBackendUrl()}/api/trendyol/categories/${id}/attributes`, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error([body.error, body.details].filter(Boolean).join(" — ") || "Özellikler alınamadı");
      const list = (body.data?.attributes ?? []) as CategoryAttribute[];
      setAttrs(list);
      setSources(Object.fromEntries(list.map((a) => [String(a.id), guessSource(a)])));
    } catch (e) {
      setAttrsError(e instanceof Error ? e.message : "Özellikler alınamadı");
    } finally {
      setAttrsLoading(false);
    }
  }

  // Urunden gelecek alanlar sablonda bos kalir — builder dolduracak
  const missingRequired = attrs.filter(
    (a) => a.required
      && (sources[String(a.id)] ?? "fixed") === "fixed"
      && !picked[String(a.id)]?.valueId && !picked[String(a.id)]?.value?.trim(),
  );

  async function save() {
    if (!name.trim() || !categoryId) return;
    setSaving(true); setMsg(null);
    try {
      const trendyolAttributes = attrs
        .map((a) => {
          if ((sources[String(a.id)] ?? "fixed") !== "fixed") return null;
          const p = picked[String(a.id)];
          if (!p || (!p.valueId && !p.value?.trim())) return null;
          return {
            attributeId: a.id,
            attributeName: a.name,
            ...(p.valueId ? { attributeValueId: p.valueId } : {}),
            ...(p.value?.trim() ? { customAttributeValue: p.value.trim() } : {}),
          };
        })
        .filter(Boolean);

      const res = await fetch(`${getBackendUrl()}/api/listing-templates`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          description,
          price: price ? Number(price) : null,
          quantity: Number(quantity) || 1,
          category: selectedCat?.path ?? null,
          trendyolCategoryId: categoryId,
          trendyolAttributes,
          attributes: {
            // Builder'in dolduracagi alanlar: {attributeId: kaynak}
            attributeSources: Object.fromEntries(
              attrs
                .filter((a) => (sources[String(a.id)] ?? "fixed") !== "fixed")
                .map((a) => [String(a.id), { source: sources[String(a.id)], name: a.name, allowCustom: a.allowCustom, varianter: a.varianter }]),
            ),
            titlePattern: titlePattern.trim() || undefined,
            descriptionPattern: descriptionPattern.trim() || undefined,
            priceMultiplier: Number(priceMultiplier) || undefined,
            commissionPercent: Number(commissionPercent) || undefined,
          },
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || "Kaydedilemedi");
      }
      setMsg("Şablon kaydedildi");
      onSaved();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <button onClick={onBack} className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Şablonlar
      </button>

      <div className="mb-5">
        <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-orange-500 text-[10px] font-bold text-white">t</span>
          Yeni Trendyol Şablonu
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Trendyol'da liste yapısı kategoriye bağlı: önce kategori seçilir, sonra o kategorinin
          zorunlu özellikleri doldurulur.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Kategori secimi */}
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">1 · Kategori</h2>

          {selectedCat ? (
            <div className="mb-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-emerald-900">{selectedCat.name}</p>
                <p className="truncate text-[10px] text-emerald-700" title={selectedCat.path}>{selectedCat.path}</p>
              </div>
              <button
                onClick={() => { setCategoryId(null); setAttrs([]); setPicked({}); }}
                className="text-[10px] text-emerald-700 hover:underline"
              >
                Değiştir
              </button>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Kategori ara (ör. kolye)"
                  className="w-full rounded-lg border border-gray-200 py-1.5 pl-7 pr-2 text-sm"
                />
              </div>

              {catsLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Kategoriler yükleniyor…
                </div>
              ) : catsError ? (
                <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {catsError}
                </p>
              ) : (
                <div className="max-h-72 space-y-0.5 overflow-y-auto">
                  {results.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setCategoryId(c.id); void loadAttributes(c.id); }}
                      className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-indigo-50"
                    >
                      <span className="font-medium text-gray-800">{c.name}</span>
                      <span className="block truncate text-[10px] text-gray-400">{c.path}</span>
                    </button>
                  ))}
                  {results.length === 0 && (
                    <p className="py-6 text-center text-xs text-gray-400">Eşleşen kategori yok</p>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* Kategorinin alanlari */}
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">
            2 · Kategori alanları
            {attrs.length > 0 && (
              <span className="ml-1.5 font-normal text-gray-400">
                {attrs.filter((a) => a.required).length} zorunlu / {attrs.length} toplam
              </span>
            )}
          </h2>

          {!categoryId ? (
            <p className="rounded-lg bg-gray-50 px-3 py-6 text-center text-xs text-gray-400">
              Önce kategori seçin
            </p>
          ) : attrsLoading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Alanlar alınıyor…
            </div>
          ) : attrsError ? (
            <p className="flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {attrsError}
            </p>
          ) : attrs.length === 0 ? (
            <p className="rounded-lg bg-gray-50 px-3 py-6 text-center text-xs text-gray-400">
              Bu kategoride tanımlı özellik yok
            </p>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto">
              {/* Zorunlular once: yayin bunlarsiz reddediliyor */}
              {[...attrs].sort((a, b) => Number(b.required) - Number(a.required)).map((a) => {
                const key = String(a.id);
                const cur = picked[key] ?? {};
                const src = sources[key] ?? "fixed";
                return (
                  <div key={key}>
                    <label className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium text-gray-700">
                      {a.name}
                      {a.required && <span className="rounded bg-red-100 px-1 text-[9px] text-red-700">zorunlu</span>}
                      {a.varianter && <span className="rounded bg-indigo-100 px-1 text-[9px] text-indigo-700">varyasyon</span>}
                      {a.allowCustom && <span className="rounded bg-gray-100 px-1 text-[9px] text-gray-500">özel değer</span>}
                      <select
                        value={src}
                        onChange={(e) => setSources((p) => ({ ...p, [key]: e.target.value as AttrSource }))}
                        className="ml-auto rounded border border-gray-200 bg-white px-1 py-0.5 text-[9px] text-gray-600"
                      >
                        {(Object.keys(SOURCE_LABEL) as AttrSource[]).map((k) => (
                          <option key={k} value={k}>{SOURCE_LABEL[k]}</option>
                        ))}
                      </select>
                    </label>

                    {src !== "fixed" ? (
                      <p className="rounded border border-dashed border-indigo-200 bg-indigo-50/60 px-2 py-1 text-[10px] text-indigo-700">
                        Bu alan her üründe değişiyor — Listing Builder katalog ürününden dolduracak
                        {a.values.length > 0 ? " (kategori değer listesine eşlenerek)" : ""}.
                      </p>
                    ) : a.values.length > 0 ? (
                      <select
                        value={cur.valueId ?? ""}
                        onChange={(e) => setPicked((p) => ({
                          ...p,
                          [key]: e.target.value ? { valueId: Number(e.target.value) } : {},
                        }))}
                        className={`w-full rounded border px-2 py-1 text-xs ${
                          a.required && !cur.valueId ? "border-red-300 bg-red-50" : "border-gray-200"
                        }`}
                      >
                        <option value="">Seçiniz</option>
                        {a.values.map((v) => <option key={v.id ?? v.name} value={v.id ?? ""}>{v.name}</option>)}
                      </select>
                    ) : (
                      <input
                        value={cur.value ?? ""}
                        onChange={(e) => setPicked((p) => ({ ...p, [key]: { value: e.target.value } }))}
                        placeholder={a.allowCustom ? "Serbest metin" : "Değer"}
                        className={`w-full rounded border px-2 py-1 text-xs ${
                          a.required && !cur.value?.trim() ? "border-red-300 bg-red-50" : "border-gray-200"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Sablon bilgileri */}
      <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-semibold text-gray-900">3 · Şablon bilgileri</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="text-[11px] font-medium text-gray-600 sm:col-span-3">
            Şablon adı *
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Gümüş Kolye - Standart"
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-[11px] font-medium text-gray-600">
            Fiyat (TL)
            <input value={price} onChange={(e) => setPrice(e.target.value)} className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-[11px] font-medium text-gray-600">
            Stok
            <input value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm" />
          </label>
        </div>
        <label className="mt-3 block text-[11px] font-medium text-gray-600">
          Açıklama
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
      </section>

      {/* Builder kurallari */}
      <section className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-gray-900">4 · Listing Builder kuralları</h2>
        <p className="mb-2 text-[11px] text-gray-500">
          Katalog ürünü bu şablonla açıldığında başlık/açıklama kalıpları doldurulur.
          Değişkenler: <code>{"{urun}"}</code> <code>{"{marka}"}</code> <code>{"{materyal}"}</code>{" "}
          <code>{"{renk}"}</code> <code>{"{sku}"}</code> <code>{"{aciklama}"}</code>
        </p>
        <label className="block text-[11px] font-medium text-gray-600">
          Başlık kalıbı
          <input
            value={titlePattern}
            onChange={(e) => setTitlePattern(e.target.value)}
            placeholder="{urun} {materyal} Kolye"
            className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="mt-3 block text-[11px] font-medium text-gray-600">
          Açıklama kalıbı
          <textarea
            value={descriptionPattern}
            onChange={(e) => setDescriptionPattern(e.target.value)}
            rows={4}
            placeholder="{urun}&#10;Materyal: {materyal}&#10;Renk seçenekleri: {renk}&#10;&#10;{aciklama}"
            className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="text-[11px] font-medium text-gray-600">
            Fiyat çarpanı (maliyet x)
            <input value={priceMultiplier} onChange={(e) => setPriceMultiplier(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm" />
          </label>
          <label className="text-[11px] font-medium text-gray-600">
            Komisyon %
            <input value={commissionPercent} onChange={(e) => setCommissionPercent(e.target.value)}
              className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1.5 text-sm" />
          </label>
        </div>
      </section>

      {msg && <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">{msg}</p>}

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={save}
          disabled={saving || !name.trim() || !categoryId}
          className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Şablonu Kaydet
        </button>
        {missingRequired.length > 0 && (
          <span className="flex items-center gap-1 text-[11px] text-amber-700">
            <Info className="h-3 w-3" />
            {missingRequired.length} zorunlu alan boş — şablon kaydedilir ama yayından önce doldurulmalı
          </span>
        )}
      </div>
    </div>
  );
}
